import React from 'react';
import { FIG_INK, FIG_TEXT, FIG_RED } from '../figureStyle';

// ─── Sigmoid uncertainty (tiny at-a-glance sketch) ────────────────────────────
// For the astralcodexten "sigmoids" link tooltip: every exponential levels off
// eventually, but the data so far can't say WHERE. One solid curve of progress
// up to "now", then three dashed continuations of it — flattening below human
// level, just above it, or still climbing off the top — each ending in a "?".
// Same visual language as the simple-takeoff sketches: no axis lines, one
// dashed gridline, red data ink.

const VX = -20;
const VW = 312;
const VH = 114;
const PLOT_L = 8;
const PLOT_R = 292;
const PLOT_T = 6;
const PLOT_B = 100;

const LABEL_FS = 8;
const LABEL_LH = 7;

// Capability in abstract units: 0 at the plot floor, 1 at the plot ceiling.
const HUMAN_LEVEL = 0.5;
const CAP_START = 0.05; // where the solid history curve enters at the left
const CAP_NOW = 0.3; // capability at the "now" dot (still below human level)
const NOW_ACROSS = 0.52; // how far across the plot "now" sits

// The dashed continuations, as logistic curves picking up from the "now" dot.
// `ceiling` is where the S-curve flattens (vs HUMAN_LEVEL = 0.5); `rate` is
// how hard it turns — the tall ceiling with a low rate is still visibly
// climbing when it exits the right edge.
const BRANCHES = [
  { ceiling: 0.42, rate: 6 }, // levels off below human level
  { ceiling: 0.78, rate: 4 }, // levels off just above it
  { ceiling: 1.15, rate: 2.6 }, // hasn't leveled off by the right edge
];

const X_NOW = PLOT_L + NOW_ACROSS * (PLOT_R - PLOT_L);
const BRANCH_R = PLOT_R - 10; // branches stop short so the "?" marks fit

const capY = (cap: number) => PLOT_B - cap * (PLOT_B - PLOT_T);

const SAMPLES = 60;

function toPath(pts: Array<readonly [number, number]>): string {
  return pts
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`)
    .join(' ');
}

// Solid history: exponential from CAP_START at the left edge to CAP_NOW at now.
function historyPath(): string {
  const k = Math.log(CAP_NOW / CAP_START);
  const pts: Array<readonly [number, number]> = [];
  for (let i = 0; i <= SAMPLES; i++) {
    const f = i / SAMPLES;
    const cap = CAP_START * Math.exp(k * f);
    pts.push([PLOT_L + f * (X_NOW - PLOT_L), capY(cap)] as const);
  }
  return toPath(pts);
}

// Logistic continuation from (X_NOW, CAP_NOW) toward `ceiling`.
function branchCap(ceiling: number, rate: number, f: number): number {
  const a = (ceiling - CAP_NOW) / CAP_NOW;
  return ceiling / (1 + a * Math.exp(-rate * f));
}

function branchPath(ceiling: number, rate: number): string {
  const pts: Array<readonly [number, number]> = [];
  for (let i = 0; i <= SAMPLES; i++) {
    const f = i / SAMPLES;
    pts.push([X_NOW + f * (BRANCH_R - X_NOW), capY(branchCap(ceiling, rate, f))] as const);
  }
  return toPath(pts);
}

export default function SimpleSigmoidBranches() {
  return (
    <div className="mx-auto mb-4">
      <svg viewBox={`${VX} 0 ${VW} ${VH}`} className="w-full h-auto font-mono">
        <line
          x1={PLOT_L} y1={capY(HUMAN_LEVEL)} x2={PLOT_R} y2={capY(HUMAN_LEVEL)}
          stroke={FIG_INK} strokeWidth={0.75} strokeDasharray="2 3" opacity={0.55}
        />
        <text
          x={PLOT_L - 4} y={capY(HUMAN_LEVEL) + 2 - LABEL_LH / 2}
          textAnchor="end" fontSize={LABEL_FS} fill={FIG_TEXT}
        >
          <tspan x={PLOT_L - 4}>Human</tspan>
          <tspan x={PLOT_L - 4} dy={LABEL_LH}>level</tspan>
        </text>

        <path
          d={historyPath()} fill="none" stroke={FIG_RED}
          strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"
        />
        {BRANCHES.map(({ ceiling, rate }) => (
          <g key={ceiling}>
            <path
              d={branchPath(ceiling, rate)} fill="none" stroke={FIG_RED}
              strokeWidth={1.75} strokeDasharray="4 3.5"
              strokeLinecap="round" strokeLinejoin="round"
            />
            <text
              x={BRANCH_R + 4} y={capY(branchCap(ceiling, rate, 1)) + 2.5}
              fontSize={LABEL_FS + 1} fill={FIG_TEXT} opacity={0.7}
            >
              ?
            </text>
          </g>
        ))}

        <circle cx={X_NOW} cy={capY(CAP_NOW)} r={2.5} fill={FIG_INK} />
        <text
          x={X_NOW} y={capY(CAP_NOW) + 12}
          textAnchor="middle" fontSize={LABEL_FS} fill={FIG_TEXT} opacity={0.65}
        >
          now
        </text>
      </svg>
    </div>
  );
}
